import { Measurements } from '~/utility/BinSentry-ui-utility';
import { DateTime } from 'luxon';
import moment from 'moment';
import { sharedStyles } from '../../styles/shared-styles';
import { LitEntity, html, css, renderIf } from '../../utility/lit-entity';
import './device-events';
import '../props/device-event-payload-type';
import '../../common/bn-icon';

const SHOW_DETAILS_KEY = 'show-device-events-details';

const NETWORK_MAP = {
  '310 410': 'AT&T',
  '310 260': 'T-Mobile',
  '310 012': 'Verizon',
  '311 480': 'Verizon',
  '302 720': 'Rogers',
  '302 610': 'Bell',
  '302 220': 'Telus',
  '302 780': 'SaskTel',
};

class DeviceEventsList extends LitEntity {
  constructor() {
    super();
    this.events = [];
    this.dimension = Measurements.get('dimension');
    this.showDetails = window.localStorage.getItem(SHOW_DETAILS_KEY) === 'true';
  }

  static get properties() {
    return {
      start: { type: String },
      end: { type: String },
      startCurrent: { type: String, attribute: false },
      endCurrent: { type: String, attribute: false },
      events: { type: Array, attribute: false },
      prevLink: { type: Object, attribute: false },
      nextLink: { type: Object, attribute: false },
      eventsHref: { type: String, attribute: false },
      disableNext: { type: Boolean, attribute: false },
      showDetails: { type: Boolean, attribute: false },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      .number {
        text-align: right;
      }
      .table-container {
        overflow: auto;
      }
      .nowrap {
        white-space: nowrap
      }
      .flex-centered {
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .list-centered-actions {
        gap: 1rem;
        flex-grow: 1;
      }
    `];
  }

  render() {
    return html`
      ${this._renderListActions()}
      <div class="table-container">
        <table class="events">
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Distance (${this.dimension.unit})</th>
              <th>Battery (V)</th>
              <th title="Battery State of Charge">Battery SoC (%)</th>
              <th title="LTE Receive Signal Strength Indicator (cell strength)">RSSI (dBm)</th>
              <th title="LTE Reference Signal Received Power">RSRP (dBm)</th>
              <th title="LTE Reference Signal Received Quality">RSRQ (dB)</th>
              <th title="LTE Signal to Interference-Noise Ratio">SINR (dB)</th>
              <th title="Bit Error Rate">Cell BER (%)</th>
              <th>Network</th>

              ${renderIf(this.showDetails, () => html`
                <th title="Cell Tracking Area Code">Cell TAC</th>
                <th>Cell ID</th>
                <th>LTE Band</th>
                <th>LTE Channel (MHz)</th>
              `)}

              <th>Firmware</th>
              <th>Sensor Errors</th>
              <th>Last Run Status</th>
              <th title="Current Runtime Status">Cur. Runtime Status</th>
              <th>Last Runtime Status</th>
              <th title="Since Last Report Runtime Status">SLR Runtime Status</th>

              ${renderIf(this.showDetails, () => html`
                <th>Bootloader</th>
                <th>Battery °C</th>
                <th>Sensor °C</th>
                <th>MCU °C</th>

                <th>Modem 1 °C</th>
                <th>Modem 2 °C</th>
                <th>Modem 3 °C</th>
                <th>Last Activity Before Reset</th>

                <th>Reset Reasons</th>
                <th title="Sensor Measurement Attempts">Sensor Attempts</th>
                <th title="Sensor Measurement Time">Sensor Time (s)</th>
                <th title="Longest Sensor Measurement Time Since Last Report">Longest Sensor Time SLR (s)</th>
                <th>Network Connect Time (s)</th>
                <th title="Longest Network Connect Time Since Last Report">Longest Network Connect SLR (s)</th>
                <th>MQTT Connect Time (s)</th>
                <th>Longest MQTT Connect Time SLR (s)</th>
                <th>Publish Attempts</th>
                <th>Until Publish Uptime (s)</th>
                <th>ICCID Read Time (s)</th>
                <th>IMEI Read Time (s)</th>
                <th>Last Publish Attempts</th>
                <th title="Total Publish Attempts Since Last Report">Total Publish Attempts SLR</th>
                <th>Last Report Publish Time (s)</th>
                <th>Longest Report Publish Time SLR (s)</th>
                <th>Last Shadow Update Time (s)</th>
                <th>Longest Shadow Update Time SLR (s)</th>
                <th>Last Total Uptime (s)</th>
                <th>Longest Total Uptime SLR (s)</th>
                <th>Cumulative Uptime SLR (s)</th>
                <th>Wake-ups SLR</th>
                <th>Brownouts SLR</th>
                <th>Watchdog Resets SLR</th>
                <th>Hard Faults SLR</th>
                <th>Config Asserts SLR</th>
                <th>MCU Faults SLR</th>
                <th>Publish Start Datetime</th>
                <th>Measurement Delta Time (s)</th>
                <th>Debug 1</th>
                <th>Debug 2</th>
                <th>Debug 3</th>
                <th>Debug 4</th>
                <th>Debug 5</th>
                <th>Debug 6</th>
                <th>Image CRC32</th>
                <th>Report CRC32</th>
              `)}
            </tr>
          </thead>
          <tbody>
            ${this.events.map(this._renderEvent, this)}
          </tbody>
        </table>
      </div>
      ${this._renderListActions()}
      <device-events
        .href="${this.eventsHref}"
        .start="${this.start}"
        .end="${this.end}"
        @update="${this._onUpdateLevels}"
      ></device-events>
    `;
  }

  _renderEvent(event) {
    const { properties } = event;
    const { date, batVolt, payloadType, distance, cellStrength, networkOperator, sensorCriticalError, version, lastRunError, bootloaderVersion, sensorWarning } = properties;
    const versionString = typeof version !== 'object' ? '1.0.0' : `${version.major}.${version.minor}.${version.build}`;
    const bootloaderVersionString = typeof bootloaderVersion !== 'object' ? '???' : `${bootloaderVersion.major}.${bootloaderVersion.minor}.${bootloaderVersion.build}`;
    let sensorErrorText = 'none';
    if (typeof sensorCriticalError === 'object') {
      sensorErrorText = sensorCriticalError.title;
    } else if (typeof sensorWarning === 'object') {
      sensorErrorText = sensorWarning.title;
    }
    const resetReasonText = typeof properties.resetReasons === 'object' ? properties.resetReasons.title : '???';

    const coalescedCurrentRuntimeStatus = properties.currentRuntimeStatus
      ? html`<td title="${this.renderUIntToTitle(properties.currentRuntimeStatus && BigInt(properties.currentRuntimeStatus.code), 8)}">${properties.currentRuntimeStatus?.title || 'none'}</td>`
      : html`<td title="${this.renderUIntToTitle(properties.runtimeStatus, 2)}">${properties.runtimeStatus ? `0x${properties.runtimeStatus.toString(16)}` : '???'}</td>`;

    return html`
      <tr class="event">
        <td class="nowrap">${this._formatDateTime(date)}</td>
        <td title="${payloadType}"><device-event-payload-type payload-type="${payloadType}"></device-event-payload-type></td>
        <td class="number">${this.dimension.convert({ dimension: (distance / 100) }, 3)}</td>
        <td class="number">${this._orUnknown(batVolt)}</td>

        <td class="number">${this._orUnknown(properties.batteryStateOfCharge_pct)}</td>
        <td class="number">${this._orUnknown(cellStrength /* RSSI */)}</td>
        <td class="number">${this._orUnknown(properties.cellStrengthRSRP_dBm)}</td>
        <td class="number">${this._orUnknown(properties.cellStrengthRSRQ_dB)}</td>
        <td class="number">${this._orUnknown(properties.cellStrengthSNR_dB)}</td>
        <td class="number">${this._unknownUndefinedOrMap(properties.cellBER_pctX100, v => v / 100)}</td>
        <td title="${networkOperator}">${NETWORK_MAP[networkOperator] || networkOperator}</td>
        ${renderIf(this.showDetails, () => html`
          <td class="number">${this._unknownUndefinedOrMap(properties.cellTrackingAreaCode, this.renderUIntHex)}</td>
          <td class="number">${this._unknownUndefinedOrMap(properties.cellTowerID, v => this.renderUIntHex(v, 8))}</td>
          <td class="number">${this._orUnknown(properties.lteBand)}</td>
          <td class="number">${this._orUnknown(properties.lteChannelID)}</td>
        `)}
        <td>${versionString}</td>
        <td title="${this.renderUIntToTitle(properties.sensorErrorCode)}">${sensorErrorText}</td>
        <td title="${this.renderUIntToTitle(lastRunError?.code)}">${lastRunError ? lastRunError.title : 'none'}</td>
        ${coalescedCurrentRuntimeStatus}
        <td title="${this.renderUIntToTitle(properties.lastRuntimeStatus && BigInt(properties.lastRuntimeStatus.code), 8)}">${properties.lastRuntimeStatus?.title || 'none'}</td>
        <td title="${this.renderUIntToTitle(properties.runtimeStatusSinceLastReport && BigInt(properties.runtimeStatusSinceLastReport.code), 8)}">
          ${properties.runtimeStatusSinceLastReport?.title || 'none'}${properties.wakeUpCountSinceLastReport ? ` (${properties.wakeUpCountSinceLastReport})` : ''}
        </td>

        ${renderIf(this.showDetails, () => html`
          <td>${bootloaderVersionString}</td>
          <td class="number">${this._orUnknown(properties.batteryTemperature)}</td>
          <td class="number">${this._orUnknown(properties.sensorTemperature)}</td>
          <td class="number">${this._orUnknown(properties.mcuTemperature)}</td>

          <td class="number">${this._orUnknown(properties.cellModemTemperature1)}</td>
          <td class="number">${this._orUnknown(properties.cellModemTemperature2)}</td>
          <td class="number">${this._orUnknown(properties.cellModemTemperature3)}</td>
          <td title="${this.renderUIntToTitle(properties.lastActivityBeforeReset?.code)}">${this._orUnknown(properties.lastActivityBeforeReset?.title)}</td>

          <td title="${this.renderUIntToTitle(properties.resetReasons?.code)}">${resetReasonText}</td>

          <td class="number">${this._orUnknown(properties.sensorReadAttemptCount)}</td>
          <td class="number">${this._orUnknown(properties.sensorMeasurementTime_sec)}</td>
          <td class="number">${this._orUnknown(properties.longestSensorMeasurementTimeSinceLastReport_sec)}</td>

          <td class="number">${this._orUnknown(properties.networkConnectionTimeSeconds)}</td>
          <td class="number">${this._orUnknown(properties.longestNetworkConnectTimeSinceLastReport_sec)}</td>
          <td class="number">${this._orUnknown(properties.mqttConnectTime_sec)}</td>
          <td class="number">${this._orUnknown(properties.longestMQTTConnectTimeSinceLastReport_sec)}</td>
          <td class="number">${this._orUnknown(properties.publishAttemptCount)}</td>
          <td class="number">${this._orUnknown(properties.upTimeSeconds)}</td>
          <td class="number">${this._orUnknown(properties.simICCIDReadTime_sec)}</td>
          <td class="number">${this._orUnknown(properties.cellModemIMEIReadTime_sec)}</td>
          <td class="number">${this._orUnknown(properties.lastMQTTReportPublishAttemptCount)}</td>
          <td class="number">${this._orUnknown(properties.mqttReportPublishAttemptCountSinceLastReport)}</td>
          <td class="number">${this._orUnknown(properties.lastMQTTReportPublishTime_sec)}</td>
          <td class="number">${this._orUnknown(properties.longestMQTTReportPublishTimeSinceLastReport_sec)}</td>
          <td class="number">${this._orUnknown(properties.lastShadowUpdateTime_sec)}</td>
          <td class="number">${this._orUnknown(properties.longestShadowUpdateTimeSinceLastReport_sec)}</td>
          <td class="number">${this._orUnknown(properties.lastTotalUptime_sec)}</td>
          <td class="number">${this._orUnknown(properties.longestTotalUptimeSinceLastReport_sec)}</td>
          <td class="number">${this._orUnknown(properties.cumulativeUptimeSinceLastReport_sec)}</td>
          <td class="number">${this._orUnknown(properties.wakeUpCountSinceLastReport)}</td>
          <td class="number">${this._orUnknown(properties.brownoutCountSinceLastReport)}</td>
          <td class="number">${this._orUnknown(properties.watchdogResetCountSinceLastReport)}</td>
          <td class="number">${this._orUnknown(properties.hardFaultCountSinceLastReport)}</td>
          <td class="number">${this._orUnknown(properties.configAssertCountSinceLastReport)}</td>
          <td title="${this.renderUIntToTitle(properties.mcuFaultFlagsSinceLastReport?.code)}">${properties.mcuFaultFlagsSinceLastReport?.title || 'none'}</td>
          <td class="nowrap" title="time: ${properties.publishStartCTime_sec} | isUTC: ${properties.publishStartCTimeIsUTC}">
              ${this._unknownUndefinedOrMap(properties.publishStartCTime_sec, v => `${DateTime.fromSeconds(v).toISO({ suppressMilliseconds: true })} ${properties.publishStartCTimeIsUTC === 0 ? ' L' : ''}`)}
          </td>
          <td class="number">${this._orUnknown(properties.measurementDeltaFromPublishStart_sec)}</td>
          <td class="number" title="${this.renderDebugToTitle(properties.debug1, 4)}">${this._orUnknown(properties.debug1)}</td>
          <td class="number" title="${this.renderDebugToTitle(properties.debug2, 4)}">${this._orUnknown(properties.debug2)}</td>
          <td class="number" title="${this.renderDebugToTitle(properties.debug3, 4)}">${this._orUnknown(properties.debug3)}</td>
          <td class="number" title="${this.renderDebugToTitle(properties.debug4, 4)}">${this._orUnknown(properties.debug4)}</td>
          <td class="number" title="${this.renderDebugToTitle(properties.debug5, 4)}">${this._orUnknown(properties.debug5)}</td>
          <td class="number" title="${this.renderDebugToTitle(properties.debug6, 1)}">${this._orUnknown(properties.debug6)}</td>
          <td class="number">${this._orUnknown(this.renderUIntHex(properties.imageCRC32, 8))}</td>
          <td class="number">${this._orUnknown(this.renderUIntHex(properties.reportPayloadCRC32, 8))}</td>
        `)}
      </tr>
    `;
  }

  _renderListActions() {
    return html`
      <div class="list-actions flex-centered">
        <div class="list-centered-actions flex-centered">
          ${renderIf(this.prevLink, html`
            <button class="button button--tertiary" @click="${this._prev}">Prev</button>
            <span class="date">${this._formatDateTime(this.startCurrent)}</span>
          `)}
          ${renderIf(this.prevLink && this.nextLink, ' - ')}
          ${renderIf(this.nextLink, html`
            <span class="date">${this._formatDateTime(this.endCurrent)}</span>
            <button class="button button--tertiary" @click="${this._next}" ?disabled="${this.disableNext}">Next</button>
          `)}
        </div>
        <label class="flex-centered">
          <input
            type="checkbox"
            ?checked=${this.showDetails}
            @input="${this._toggleDetails}"
          />
          <span title="label">Show Details</span>
        </label>
      </div>
    `;
  }

  renderDebugToTitle(num, byteLength) {
    if (typeof num === 'undefined') {
      return '';
    }
    const bitLength = byteLength * 8;
    const binary = [...num.toString(2).padStart(bitLength, '0')].map((d, i) => (i) % 8 === 0 ? ` ${d}` : d).join('').trim();
    // eslint-disable-next-line no-bitwise
    return `${num} | ${this.renderUIntHex(num, byteLength * 2)}\n${binary}\nsigned: ${(num << bitLength) >> bitLength}`;
  }

  renderUIntToTitle(uInt, byteLength = 1) {
    if (typeof uInt === 'undefined' || uInt === 'unknown') {
      return '';
    }
    const num = ['number', 'bigint'].includes(typeof uInt) ? uInt : parseInt(uInt, 10);
    const binary = [...num.toString(2).padStart(byteLength * 8, '0')].map((d, i) => (i) % 8 === 0 ? ` ${d}` : d).join('').trim();
    return `${num} | ${this.renderUIntHex(num, byteLength * 2)}\n${binary}`;
  }

  renderUIntHex(uInt, length = 4) {
    return uInt === undefined ? uInt : `0x${uInt.toString(16).toUpperCase().padStart(length, '0')}`;
  }

  _toggleDetails() {
    this.showDetails = !this.showDetails;
    localStorage.setItem(SHOW_DETAILS_KEY, this.showDetails);
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    this.eventsHref = this.getHref('https://api.binsentry.com/rel/events');
  }

  _orUnknown(value) {
    return value === undefined ? html`<span title="unknown">???</span>` : html`${value}`;
  }

  _unknownUndefinedOrMap(value, mapper) {
    // undefined if the packet has no knowledge of the field
    if (value === undefined) {
      return this._orUnknown(value);
    }
    // usually 'unknown' when the device specifically reports the field as unknown
    if (value === 'unknown') {
      return value;
    }
    return mapper ? mapper(value) : value;
  }

  _prev() {
    if (!this.prevLink) {
      return;
    }
    this.eventsHref = this.prevLink.href;
  }

  _next() {
    if (!this.nextLink) {
      return;
    }
    this.eventsHref = this.nextLink.href;
  }

  async _onUpdateLevels(e) {
    const { entity } = e.detail;
    if (!entity) {
      this.events = [];
      return;
    }

    this.prevLink = entity.getLinkByRel('prev');
    this.nextLink = entity.getLinkByRel('next');

    this.startCurrent = entity.properties.startDate;
    this.endCurrent = entity.properties.endDate;
    this.dispatchEvent(new CustomEvent('update', { detail: { entity } }));
    this.disableNext = moment(this.endCurrent).add(1, 'hour').isAfter(moment());
    this.events = entity.getSubEntitiesByClass('event');
  }

  _formatDateTime(date) {
    return DateTime.fromISO(date).toFormat('MMM dd, yyyy HH:mm');
  }
}

window.customElements.define('device-events-list', DeviceEventsList);
